import React, {ReactNode, useCallback, useMemo, useState} from 'react';
import {signUp} from 'aws-amplify/auth';
import ImageLogo from '../common/resources/NarrowLogo.svg?react';
import {Authenticator, useAuthenticator} from '@aws-amplify/ui-react';
// @ts-expect-error: No type def
import ReCAPTCHA from "react-google-recaptcha";
import '@aws-amplify/ui-react/styles.css';
import {useLocation} from 'react-router-dom';
import '../App.css';
import './Login.css';
import {useDarkMode} from "../temporary/localStorageUtils.ts";
import CenterRow from "../components/row/CenterRow.tsx";
import {create} from "zustand";
import * as Sentry from "@sentry/react";
import Button from "../components/interaction/Button.tsx";
import {FcGoogle} from "react-icons/fc";
import {cognitoConfig} from "./cognito_confg.ts";
import {authorizeByToken} from "./authorizeByToken.ts";
import {randomStr} from "../common/random.ts";
import {Bold} from "../components/text/Bold.tsx";

export const TOWNGEN_API = 'TOWN_GEN_API';

function useQuery() {
  const {search} = useLocation();

  return React.useMemo(() => new URLSearchParams(search), [search]);
}

interface CaptchaState {
  verified: boolean,
  setVerified: (v: boolean) => void,
}

const useCaptcha = create<CaptchaState>()(set => ({
  verified: false,
  setVerified: (verified) => set({verified}),
}));

const inIframe = window.self !== window.top;

async function signInWithCode(code: string) {
  const oauthConfig = cognitoConfig.loginWith!.oauth;

  const redirectUri = oauthConfig.customOauthRedirectUri;
  const clientId = cognitoConfig.userPoolClientId;

  const oAuthTokenBody = {
    grant_type: 'authorization_code',
    code,
    client_id: clientId,
    redirect_uri: redirectUri,
  };

  const oAuthTokenEndpoint = 'https://' + oauthConfig.domain + '/oauth2/token';

  const body = Object.entries(oAuthTokenBody)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');
  const {
    access_token,
    refresh_token,
    id_token,
  } = await (
    await fetch(oAuthTokenEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body,
    })
  ).json();

  await authorizeByToken(
    {idToken: id_token, accessToken: access_token, refreshToken: refresh_token},
    {userPoolId: cognitoConfig.userPoolId, clientId}
  );

  // reload the page to pick up the new tokens
  window.location.reload();
}

const useLoginCode = () => {
  const {search} = useLocation();

  return useMemo(() => {
    const context = new URLSearchParams(search).get('loginCode');
    return context === 'true';

  }, [search]);
};

function IframeGoogleSignIn() {
  const [oAuthState, setOAuthState] = useState<string>()
  const loginCode = useLoginCode();

  const onClick = useCallback(() => {
    const oauthConfig = cognitoConfig.loginWith!.oauth;

    const authHost = `https://${oauthConfig.domain}`;
    const identityProvider = 'Google';
    const redirectUri = oauthConfig.customOauthRedirectUri;
    const responseType = 'code';
    const clientId = cognitoConfig.userPoolClientId;
    const state = randomStr();
    const scope = oauthConfig.scopes.join(" ");

    const authUrl = `${authHost}/oauth2/authorize?identity_provider=${identityProvider}&redirect_uri=${redirectUri}&response_type=${responseType}&client_id=${clientId}&state=${state}&scope=${scope}`

    setOAuthState(state);

    window.addEventListener("message", async (event) => {

      // Security check of sorts, we only want to handle messages from our own stuff
      if (event.origin !== window.location.origin) return;

      if (event.data && event.data.type === "google-auth-success") {
        const {code} = event.data.urlParams;
        signInWithCode(code);
      }
    });

    window.open(
      authUrl,
      "awsGoogleAuth",
      "location,toolbar,resizable,scrollbars,status,width=600,height=600"
    );
  }, [])

  const [userLoginCode, setUserLoginCode] = useState<string>('');

  return (
    <>
      <div style={{display: "grid"}}>
        <Button style={{margin: "0 32px"}} onClick={onClick}>
          <FcGoogle style={{marginRight: "8px"}} size={20}/>
          Sign in with Google
        </Button>
      </div>
      {oAuthState && loginCode && (
        <div style={{margin: "12px 32px 0"}}>
          <div><Bold>Request ID: {oAuthState}</Bold></div>
          <div style={{display: "grid", gridTemplateColumns: "1fr max-content", alignItems: "center", gap: "8px"}}>
            <input
              placeholder="Login code"
              className="form-input"
              autoComplete="nope"
              type="text"
              value={userLoginCode}
              onChange={e => setUserLoginCode(e.target.value.trim())}
            />
            <Button onClick={() => signInWithCode(userLoginCode)}>
              Login
            </Button>
          </div>
        </div>
      )}
    </>
  )
}

const components = {
  Header() {
    return (
      <ImageLogo className="login-logo-img"/>
    );
  },
  SignIn: {
    Header() {
      return inIframe ? <IframeGoogleSignIn/> : null
    }
  },
  SignUp: {
    FormFields() {
      const darkMode = useDarkMode();
      const {setVerified} = useCaptcha();
      const {updateForm} = useAuthenticator()

      return (
        <>
          {/* Re-use default `Authenticator.SignUp.FormFields` */}
          <Authenticator.SignUp.FormFields/>
          <CenterRow>
            <ReCAPTCHA sitekey="6LfVtqArAAAAAF8X8kIYjMTpfcF2o5QpF7s_Zr38"
                       onChange={() => {
                         setVerified(true);
                         updateForm();
                       }}
                       onExpired={() => setVerified(false)}
                       theme={darkMode ? 'dark' : 'light'}/>
          </CenterRow>
        </>
      );
    },
  }
};

function useInitialState(): 'signIn' | 'signUp' {
  const query = useQuery();
  const state = query.get('authState');
  if (state === 'signIn' || state === 'signUp') {
    return state
  }
  if (state === null) {
    return 'signIn'
  }
  throw new Error("Unknown auth state");
}


function Authenticated({children}: { children: ReactNode }) {
  const initialState = useInitialState()
  return (
    <Authenticator loginMechanisms={['username', 'email']} signUpAttributes={['email']} socialProviders={inIframe ? [] : ['google']}
                   className="authenticator" initialState={initialState}
                   components={components} services={{
      async handleSignUp(formData) {
        if (!useCaptcha.getState().verified) {
          return Promise.reject("Must not be a robot - verify via captcha");
        }
        const {username, password, options} = formData;
        return signUp({
          username,
          password,
          options,
        });
      },
    }}>
      {({user}) => {
        Sentry.setUser({id: user?.userId || 'unknown'});
        return (
          <>
            {children}
          </>
        );
      }}
    </Authenticator>
  );
}

export default Authenticated;
